// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import './Card.css';

type CardProps = Readonly<{
  children: React.ReactNode;
  className?: string;
}>;

export function Card({ children, className = '' }: CardProps) {
  return (
    <div className={`card ${className}`}>
      {children}
    </div>
  );
}

type CardHeaderProps = Readonly<{
  children: React.ReactNode;
  className?: string;
  noBorder?: boolean;
}>;

export function CardHeader({ children, className = '', noBorder = false }: CardHeaderProps) {
  return (
    <div className={`card-header ${noBorder ? '' : 'card-header--bordered'} ${className}`}>
      {children}
    </div>
  );
}

interface TableColumnConfig {
  label: string;
  className?: string;
}

type TableHeaderProps = Readonly<{
  columns: (string | TableColumnConfig)[];
  className?: string;
}>;

export function TableHeader({ columns, className = '' }: TableHeaderProps) {
  return (
    <div className={`w-full flex bg-lilac-05 border-b border-neutral-05 ${className}`}>
      {columns.map((col, index) => {
        const label = typeof col === 'string' ? col : col.label;
        const colClassName = typeof col === 'string' ? 'flex-1' : (col.className || 'flex-1');
        return (
          <div
            key={index}
            className={`${colClassName} p-md flex items-center gap-sm ${index < columns.length - 1 ? 'border-r border-purple-05' : ''}`}
          >
            <div className="text-black text-body-03 font-medium font-sans break-words">{label}</div>
          </div>
        );
      })}
    </div>
  );
}

type TableCellPrimaryProps = Readonly<{
  children: React.ReactNode;
  className?: string;
}>;

export function TableCellPrimary({ children, className = 'flex-1' }: TableCellPrimaryProps) {
  return (
    <div className={`${className} px-md py-sm flex items-center`}>
      <div style={{ overflow: 'hidden', color: '#141414', textOverflow: 'ellipsis', fontFamily: 'Google Sans', fontSize: 14, fontStyle: 'normal', fontWeight: 500, lineHeight: 'normal' }}>
        {children}
      </div>
    </div>
  );
}

type TableCellProps = Readonly<{
  children: React.ReactNode;
  className?: string;
}>;

export function TableCell({ children, className = 'flex-1' }: TableCellProps) {
  return (
    <div className={`${className} px-md py-sm flex items-center`}>
      <div style={{ overflow: 'hidden', color: '#141414', textOverflow: 'ellipsis', fontFamily: 'Google Sans', fontSize: 14, fontStyle: 'normal', fontWeight: 400, lineHeight: 'normal' }}>
        {children}
      </div>
    </div>
  );
}

type CardBodyProps = Readonly<{
  children: React.ReactNode;
  className?: string;
  noPadding?: boolean;
}>;

export function CardBody({ children, className = '', noPadding = false }: CardBodyProps) {
  return (
    <div className={`card-body ${noPadding ? '' : 'card-body--padded'} ${className}`}>
      {children}
    </div>
  );
}
